import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from '@naf-simba/elements-buttons';
import { RadioButton } from '@naf-simba/elements-radio-button';
import { Input } from '@naf/input';
import { Text, TextVariant } from '@naf/text';
import { formIds, FormType } from '../../../../types/formType';
import { actions as userActions, FormSubmissionStatus } from '../../redux/modules/user';
import useSelector from '../../redux/typedHooks';
import BlockContent from '../block-content/BlockContent';
import SimpleTextLink from '../simple-textlink/SimpleTextLink';
import ThumbsUp from '../../../assets/images/ThumbsUp.svg?url';
import S from './StyledFeedbackSurvey';

type CustomTexts = {
  positiveRadioLabel?: string;
  negativeRadioLabel?: string;
  positiveTextAreaPlaceholder?: string;
  negativeTextAreaPlaceholder?: string;
};

interface Props {
  contentUrl: string;
  title?: string;
  customText?: CustomTexts;
  formId?: formIds;
  showResponseBody?: boolean;
  showContactButton?: boolean;
}

const FeedbackSurvey = ({
  contentUrl,
  title = 'Fant du det du lette etter?',
  customText,
  formId = formIds.FEEDBACK_FORM,
  showResponseBody = true,
  showContactButton = true,
}: Props) => {
  const texts = {
    positiveRadioLabel: 'Ja',
    negativeRadioLabel: 'Nei',
    positiveTextAreaPlaceholder: 'Så bra! Er det noe på nettsiden som kunne vært bedre?',
    negativeTextAreaPlaceholder: 'Hva lette du etter?',
    ...customText,
  };

  const dispatch = useDispatch();
  const formSubmissionStatus = useSelector((state) => state.user.formSubmissionStatus);
  const suggestedResponse = useSelector((state) => state.user.formSuggestedResponse);
  const [radioBtnValue, setRadioBtnValue] = useState('');
  const [textAreaValue, setTextAreaValue] = useState('');
  const [isAnswerSent, setIsAnswerSent] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);

  const isSubmitting = formSubmissionStatus === FormSubmissionStatus.Validating;

  const POSITIVE_VALUE = 'yes';
  const NEGATIVE_VALUE = 'no';
  const contentIsUseful = radioBtnValue === POSITIVE_VALUE;

  const handleRadioChange = (value: string) => {
    setRadioBtnValue(value);
    setSubmissionError(false);
  };

  useEffect(() => {
    if (formSubmissionStatus === FormSubmissionStatus.Validating) {
      return;
    }

    if (
      formSubmissionStatus === FormSubmissionStatus.Validated ||
      formSubmissionStatus === FormSubmissionStatus.Posting ||
      formSubmissionStatus === FormSubmissionStatus.Success
    ) {
      setIsAnswerSent(true);
      setSubmissionError(false);
    } else if (
      formSubmissionStatus === FormSubmissionStatus.ValidationFailed ||
      formSubmissionStatus === FormSubmissionStatus.Error
    ) {
      setSubmissionError(true);
    }
  }, [formSubmissionStatus]);

  const dispatchSetUserFeedback = () => {
    const numberValue = contentIsUseful ? 1 : -1;
    // GTM tracking
    dispatch(
      userActions.setUserFeedback({
        contentUrl,
        contentUseful: contentIsUseful,
        contentComment: textAreaValue,
        contentValue: numberValue,
      }),
    );

    // * After the form values are sendt to Google Analytics, we need to post it to contenthub api
    const formPayload: FormType = {
      formId,
      fieldValues: [
        { fieldName: 'fromUrl', value: contentUrl },
        { fieldName: 'yesOrNo', value: contentIsUseful ? POSITIVE_VALUE : NEGATIVE_VALUE },
        { fieldName: 'feedback', value: textAreaValue },
      ],
    };
    dispatch(userActions.validateForm.request(formPayload));
  };

  const onClickSendAnswer = () => {
    if (isSubmitting) {
      return;
    }

    setSubmissionError(false);
    dispatchSetUserFeedback();
  };

  return (
    <S.Wrapper s="12" m="12" l="12" xl="12">
      <S.HRLine />
      {!isAnswerSent ? (
        <>
          <S.Title>{title}</S.Title>
          <S.Form>
            <RadioButton
              label={texts.positiveRadioLabel}
              name="FeedbackRadio"
              value={POSITIVE_VALUE}
              checked={radioBtnValue === POSITIVE_VALUE}
              onChange={() => handleRadioChange(POSITIVE_VALUE)}
            />
            <RadioButton
              label={texts.negativeRadioLabel}
              name="FeedbackRadio"
              value={NEGATIVE_VALUE}
              checked={radioBtnValue === NEGATIVE_VALUE}
              onChange={() => handleRadioChange(NEGATIVE_VALUE)}
            />
          </S.Form>

          {radioBtnValue && (
            <>
              <S.ResponseText variant={TextVariant.ArticleTextHeader}>
                {contentIsUseful ? texts.positiveTextAreaPlaceholder : texts.negativeTextAreaPlaceholder}
              </S.ResponseText>
              <S.StyledTextArea>
                <Input
                  as="textarea"
                  placeholder="Skriv din tilbakemelding her."
                  maxCharacters={500}
                  width={450}
                  value={textAreaValue}
                  onChange={(text: string) => {
                    if (typeof text === 'string') {
                      setTextAreaValue(text);
                      setSubmissionError(false);
                    }
                  }}
                />
              </S.StyledTextArea>
              <S.TextAreaInfo>
                <S.StyledIcon size={24} />
                <Text variant={TextVariant.Small}>
                  Ikke skriv personlig informasjon.
                  <br />
                  Her svarer vi ikke, men trenger du hjelp kan du{' '}
                  <SimpleTextLink href="https://www.naf.no/kontakt-oss/">kontakte oss</SimpleTextLink>.
                </Text>
              </S.TextAreaInfo>
              <S.FormButtonWrapper>
                <Button variant="outlined" onClick={onClickSendAnswer} disabled={isSubmitting} isLoading={isSubmitting}>
                  Send svar
                </Button>
                {submissionError && (
                  <S.ErrorMessage variant={TextVariant.Small} role="alert">
                    Det oppstod en feil. Prøv igjen senere.
                  </S.ErrorMessage>
                )}
              </S.FormButtonWrapper>
            </>
          )}
        </>
      ) : (
        <S.ResponseMsg>
          <S.DecorativeColumn>
            <S.Img src={ThumbsUp} alt="Tommel opp" />
          </S.DecorativeColumn>
          <S.ResponseContent>
            <S.ResponseHeading variant={TextVariant.ArticleTextHeader}>Takk for tilbakemeldingen!</S.ResponseHeading>
            {showResponseBody && (
              <S.ResponseBody>
                {contentIsUseful
                  ? 'Dine innspill hjelper oss å lage bedre nettsider.'
                  : 'Vi beklager at du ikke fant det du lette etter.'}
              </S.ResponseBody>
            )}
            {suggestedResponse.length > 0 && (
              <S.SuggestedResponse>
                <BlockContent value={suggestedResponse} />
              </S.SuggestedResponse>
            )}
          </S.ResponseContent>
          {!contentIsUseful && showContactButton && (
            <S.ThankYouButtonWrapper $hasSuggested={suggestedResponse.length > 0}>
              <Button variant="outlined" href="https://www.naf.no/kontakt-oss/">
                Kontakt kundeservice
              </Button>
            </S.ThankYouButtonWrapper>
          )}
        </S.ResponseMsg>
      )}
    </S.Wrapper>
  );
};

export default FeedbackSurvey;
